import { useCallback, useEffect, useMemo } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import getLang from 'LearningCenterUI/utils/getLang';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';

// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../hooks/searchParamHooks';
import UsageTracker from '../utils/UsageTracker';
import { SCREENS } from '../constants/Tracking';
import { fetchLessonIfNeeded
// @ts-expect-error Could not find a declaration file for module
} from '../actions/LessonActions';
import { getContentTypeForTracking } from '../utils/ContentUtils';
import LessonPage from '../components/LessonPage/LessonPage';
import { getLesson
// @ts-expect-error Could not find a declaration file for module
} from '../selectors/lessonSelectors';
import ApiError from '../components/Errors/ApiError';
import LearningPageHeader from '../containers/TrackPage/LearningPageHeader';
import LearningPageSidebar from '../containers/TrackPage/LearningPageSidebar';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FILTER_TYPES } from '../constants/TopFilterConstants';
import { useFullLesson } from '../features/lessons';
import { useLessonProgressById, usePlaylistProgress } from '../features/userProgress';
import maxBy from 'hs-lodash/maxBy';
import findIndex from 'hs-lodash/findIndex';
import { TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS } from '../constants/Navigation';
import { HasParentPlaylist, NextPlaylistContent, usePlaylistIdFromSearch } from '../features/playlistContent';
import { useAvailableLanguageOrFallback } from '../features/contentLanguage';
import useWootricSurvey from '../hooks/useWootricSurvey';
import LessonNavigator from '../components/Navigator/LessonNavigator';
import ACNavigatorButton from 'academy-common-lib/components/navigation/contentNavigator/ACNavigatorButton';
import { HR } from 'foundations-components/transitional/components/HR';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useSetLastClickedMLRecommendationItemFromUrl } from '../features/recommendations/recommendationsHooks';
import { useSingleLanguageRedirect } from '../hooks/useSingleLangaugeRedirect';
import { HasGate, useHasGate } from '../features/accounts';
import LearningContentInnerWrapper from '../features/learningContent/components/LearningContentInnerWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useCurrentLanguage = lesson => {
  const [[paramLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const selectedLessonLanguage = lesson ? lesson.get('language') || getLang() : getLang();
  return paramLanguage || selectedLessonLanguage;
};
const noRedirectRoutes = [TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS];
export const useLessonRedirectUrl = (lessonId, language, contentId) => {
  const playlistId = usePlaylistIdFromSearch();
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId, !playlistId);
  const {
    lessonProgressById,
    lessonProgressByIdError,
    lessonProgressByIdLoading
  } = useLessonProgressById(lessonId);
  const {
    lesson,
    lessonError,
    lessonLoading
  } = useFullLesson(lessonId, language);
  const {
    location
  } = useHistory();
  const lessonRedirectUrl = useMemo(() => {
    if (!lessonProgressById || !lesson) return null;

    // We never want to redirect from the pages shown at the end of a lesson
    for (const route of noRedirectRoutes) {
      if (location.pathname.includes(route)) return null;
    }
    if (contentId) {
      if (!lesson.content.some(content => content.id === contentId)) return `/lessons/${lessonId}`;else return null;
    }
    const allContentProgress = [];
    lesson.content.forEach(content => {
      const contentProgress = lessonProgressById.contentProgress.find(c => c.contentId === content.id);
      allContentProgress.push({
        contentId: content.id,
        status: contentProgress ? contentProgress.progressType : 'NOT_STARTED',
        updatedAt: contentProgress ? contentProgress.updatedAt : null,
        url: `/lessons/${lessonId}/${content.id}`
      });
    });
    let finalUrl;
    if (playlistId) {
      if (playlistProgress && playlistProgress.status !== 'COMPLETED') {
        finalUrl = `/lessons/${lessonId}/${TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT}`;
      } else {
        finalUrl = allContentProgress[0].url;
      }
    } else {
      finalUrl = `/lessons/${lessonId}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`;
    }
    allContentProgress.push({
      contentId: null,
      status: null,
      updatedAt: null,
      url: finalUrl
    });
    if (lessonProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [contentId, lesson, lessonId, lessonProgressById, location.pathname, playlistId, playlistProgress]);
  return {
    lessonRedirectUrl,
    lessonRedirectUrlLoading: lessonProgressByIdLoading || lessonLoading,
    lessonRedirectUrlError: lessonProgressByIdError || lessonError
  };
};
const getNextContentId = (lesson, contentId) => {
  if (!lesson || !lesson.get('contentIds') || !contentId) return null;
  const contentIds = lesson.get('contentIds');
  const currentIndex = contentIds.indexOf(contentId);
  return contentIds.get(currentIndex + 1, null);
};
const Lesson = () => {
  const {
    isMobile
  } = useMediaQuery();
  useWootricSurvey('CSAT');
  const dispatch = useDispatch();
  const {
    push,
    location
  } = useHistory();
  const contentById = useSelector(state => state.contentById);
  const {
    contentId,
    lessonId
  } = useParams();
  const {
    hasGate: hasUnifiedAcademy
  } = useHasGate('Academy:UnifiedAcademy:Navigation');
  const content = contentId ? contentById.get(contentId) : null;
  const contentName = content && content.get('name');
  const contentType = getContentTypeForTracking(content);

  /**
   * Future TODO:
   * - Should pass the destructed object to children instead of `lesson`
   * - This will allow us to type lesson correctly since it will no longer be an Immutable
   */
  const lesson = useSelector(state => getLesson(state, {
    lessonId
  }));
  const language = useCurrentLanguage(lesson);
  const {
    lessonRedirectUrl
  } = useLessonRedirectUrl(Number(lessonId), language, Number(contentId));
  const {
    redirectUrl: singleLanguageRedirectUrl,
    loading: singleLanguageRedirectLoading
  } = useSingleLanguageRedirect({
    contentId: Number(lessonId),
    language,
    contentType: 'LESSON'
  });
  useSetLastClickedMLRecommendationItemFromUrl({
    id: lessonId ? Number(lessonId) : undefined,
    type: 'LESSON'
  });
  const nextContentId = getNextContentId(lesson, contentId);
  const handleAdvanceContent = useCallback(() => {
    if (nextContentId) {
      push(Object.assign({}, location, {
        pathname: `/lessons/${lesson.get('id')}/${nextContentId}`
      }));
    } else {
      push(Object.assign({}, location, {
        pathname: `recommendations`
      }));
    }
  }, [push, location, lesson, nextContentId]);
  const trackResourceClick = useCallback(({
    title,
    url
  }) => {
    UsageTracker.track('contentInteraction', {
      action: 'open-resource-link',
      contentId,
      contentLanguage: language,
      contentType,
      lessonId: lesson.get('id'),
      lessonName: lesson.get('name'),
      resourceTitle: title,
      resourceUrl: url
    });
  }, [language, lesson, contentId, contentType]);
  const {
    languageToLoad,
    availableLanguagesLoading
  } = useAvailableLanguageOrFallback({
    lessonId: Number(lessonId),
    language
  });
  useEffect(() => {
    if (!availableLanguagesLoading) dispatch(fetchLessonIfNeeded(lessonId, languageToLoad));
  }, [lessonId, languageToLoad, dispatch, availableLanguagesLoading]);
  useEffect(() => {
    if (lesson && lesson.get('name')) {
      UsageTracker.track('pageView', {
        screen: SCREENS.LESSON,
        contentId,
        contentType,
        lessonId,
        lessonName: lesson.get('name'),
        hsSoftwareFilters: lesson.get('hsSoftwareFilters', []).join(', '),
        allCategories: lesson.get('subCategories', []).join(', ')
      });
    }
  }, [contentId, contentType, lesson, lessonId]);
  const {
    lesson: fullLesson,
    lessonLoading: fullLessonLoading
  } = useFullLesson(Number(lessonId), language);
  const {
    lessonProgressById
  } = useLessonProgressById(Number(lessonId));
  const assetProgress = useMemo(() => {
    if (!lessonProgressById || !lessonProgressById.contentProgress) {
      return {};
    }
    const progressMap = {};
    lessonProgressById.contentProgress.forEach(contentProgress => {
      progressMap[contentProgress.contentId] = contentProgress.progressType;
    });
    return progressMap;
  }, [lessonProgressById]);
  const instructors = useMemo(() => {
    if (!fullLesson) {
      return [];
    }
    const instructorsById = {};
    fullLesson.content.forEach(asset => {
      if (asset.professors) {
        asset.professors.forEach(instructor => {
          instructorsById[instructor.id] = instructor;
        });
      }
    });
    return Object.values(instructorsById);
  }, [fullLesson]);
  const instructorIds = instructors.map(instructor => instructor.id);
  if (!singleLanguageRedirectLoading && lesson && lesson.get('hasError')) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: lesson.get('recentError'),
      marker404: "LESSON_VIEW_API_404",
      markerDefault: "LESSON_VIEW_API_ERROR"
    });
  }
  if (singleLanguageRedirectUrl) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: {
        pathname: singleLanguageRedirectUrl,
        search: window.location.search
      }
    });
  }
  const isLoading = !lesson || availableLanguagesLoading || fullLessonLoading || !fullLesson || lesson.get('language', '').toUpperCase() !== language && lesson.get('isRequesting');
  const ratingsAdditionalTrackingData = fullLesson && {
    contentLanguage: fullLesson.language,
    contentName: fullLesson.name,
    creatorHubId: fullLesson.hubId,
    hsSoftwareFilters: fullLesson.hsSoftwareFilters,
    professorId: instructorIds,
    subCategory: fullLesson.subCategories,
    useCase: fullLesson.useCases
  };
  const navigator = fullLesson && /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LessonNavigator, {
      active: true,
      activeAssetId: Number(contentId),
      assets: fullLesson.content.map(asset => ({
        id: asset.id,
        name: asset.name,
        duration: asset.duration || 0,
        type: asset.contentType,
        linkType: asset.linkType,
        totalQuestions: asset.length || 0,
        completed: assetProgress[asset.id] === 'COMPLETED'
      })),
      duration: fullLesson.duration,
      id: Number(lessonId),
      language: language,
      name: fullLesson.name,
      useCases: fullLesson.useCases
    }), /*#__PURE__*/_jsx(HasParentPlaylist, {
      fallback: /*#__PURE__*/_jsx(ACNavigatorButton, {
        active: contentId === TRACK_RECOMMENDATIONS_URL_FRAGMENT,
        helpText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.subheading"
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.heading"
        }),
        to: `/lessons/${lessonId}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`
      }),
      children: /*#__PURE__*/_jsx(NextPlaylistContent, {
        lessonId: Number(lessonId),
        render: ({
          title,
          subtitle,
          url,
          urlFragments
        }) => /*#__PURE__*/_jsx(ACNavigatorButton, {
          active: urlFragments.includes(contentId),
          helpText: subtitle,
          title: title,
          to: url
        })
      })
    })]
  });
  const pageContent = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(View, {
      insetBlockStart: hasUnifiedAcademy ? '0' : '49px',
      position: "sticky",
      zIndex: 2,
      children: !isLoading && /*#__PURE__*/_jsx(LearningPageHeader, {
        ContentName: contentType === 'quiz' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.lessonNavigator.quiz"
        }) : contentName,
        additionalTrackingData: ratingsAdditionalTrackingData,
        availableLanguages: fullLesson.availableLanguages,
        defaultBackLink: {
          pathname: '/',
          search: ''
        },
        language: language,
        languageLinkedContent: fullLesson.languageLinkedContent,
        lessonId: lessonId
      })
    }), isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      minHeight: 200
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "LESSON_VIEW_LOADED",
      children: /*#__PURE__*/_jsxs(Flex, {
        borderColor: "border-core-default",
        borderStyle: !isMobile ? 'solid' : undefined,
        borderWidth: !isMobile ? '100' : undefined,
        flexDirection: isMobile ? 'column-reverse' : 'row',
        children: [!isMobile ? /*#__PURE__*/_jsx(LearningPageSidebar, {
          id: "sidebarNavigator",
          withSubNav: 1,
          children: navigator
        }) : /*#__PURE__*/_jsxs(View, {
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsx(HR, {
            distance: "flush"
          }), navigator]
        }), /*#__PURE__*/_jsx(LearningPageMainSection, {
          children: /*#__PURE__*/_jsx(HasParentPlaylist, {
            fallback: lesson.get('inactiveRedirectUrl') || lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
              to: {
                pathname: lesson.get('inactiveRedirectUrl') || lessonRedirectUrl,
                search: window.location.search
              }
            }) : /*#__PURE__*/_jsx(LessonPage, {
              contentId: contentId,
              lesson: lesson,
              onAdvanceContent: handleAdvanceContent,
              onResourceClick: trackResourceClick
            }),
            children: /*#__PURE__*/_jsx(NextPlaylistContent, {
              lessonId: lesson.get('id'),
              redirectUrl: lessonRedirectUrl || undefined,
              render: ({
                navigate
              }) => !isLoading && lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
                to: Object.assign({}, location, {
                  pathname: lessonRedirectUrl
                })
              }) : /*#__PURE__*/_jsx(LessonPage, {
                contentId: contentId,
                lesson: lesson,
                onAdvanceContent: nextContentId ? handleAdvanceContent : navigate,
                onResourceClick: trackResourceClick
              })
            })
          })
        })]
      })
    })]
  });
  return /*#__PURE__*/_jsx(HasGate, {
    fallback: pageContent,
    gate: "Academy:UnifiedAcademy:Navigation",
    children: /*#__PURE__*/_jsx(LearningContentInnerWrapper, {
      children: pageContent
    })
  });
};
export default Lesson;